/* eslint-disable no-unused-vars */
import "./Worker.scss";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  WorkerRatesColumns,
  WorkerLogsColumns,
  ProductOrdersColumns,
  photosApi,
} from "../../SourceData";
import StarIcon from "@mui/icons-material/Star";
import FactoriesProductsGif from "./../../assets/factories-products-gif.gif";
import AddressLocation from "./../../assets/location-icon.png";
import DeleteUser from "./../../assets/delete.svg";
import ViewUser from "./../../assets/show-user.svg";
import GreenButton from "../../components/GreenButton/GreenButton";
import Modals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { TextField, useMediaQuery } from "@mui/material";
import api from "../../utils/api";
import { Phone } from "@mui/icons-material";
import Loader from "../../components/Loader";
// No need to import Empty component

const Worker = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:767px)");
  const [openModal, setOpenModal] = useState(false);
  const [view, setView] = useState("orders");
  const [currentModalView, setCurrentModalView] = useState("userRates");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [userRatings, setUserRatings] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    getUserData();
  }, [id]);

  useEffect(() => {
    // Fetch data based on the current view
    if (view === "orders") {
      getWorkerOrders();
    } else if (view === "rates") {
      getUserRating();
    } else if (view === "logs") {
      getUserLogs();
    }
  }, [view, id, selectedDate, page, pageSize]);

  const getUserData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/technicians/${id}`);

      if (response.data) {
        setUserData(response.data.technician);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getWorkerOrders = async () => {
    try {
      setViewLoading(true);
      const response = await api.get(
        `/bookings/${id}/technician?type=completed&page=${page}&limit=${pageSize}`
      );

      if (response.data) {
        setUserOrders(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      // Reset data on error
      setUserOrders([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setViewLoading(false);
    }
  };

  const getUserRating = async () => {
    try {
      setViewLoading(true);
      const response = await api.get(
        `/ratings/${id}/technician?page=${page}&limit=${pageSize}`
      );

      if (response.data) {
        setUserRatings(response.data.data || []);
        // Change this line to look for totalDocuments instead of totalCount
        setTotalCount(response.data.totalDocuments || 0);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      // Reset data on error
      setUserRatings([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setViewLoading(false);
    }
  };

  const getUserLogs = async () => {
    try {
      setViewLoading(true);
      // Adding date filtering based on selectedDate
      const response = await api.get(
        `/admin/technician/${id}/logs/?date=${selectedDate}&page=${page}&limit=${pageSize}`
      );

      if (response.data) {
        setUserLogs(response.data.data || []);
        // Change this line to look for totalDocuments instead of totalCount
        setTotalCount(response.data.totalDocuments || 0);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (error) {
      // Reset data on error
      setUserLogs([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setViewLoading(false);
    }
  };

  const handleOpen = (view, row = null) => {
    if (row) {
      setSelectedRow(row);
    }
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleModalOpen = (view) => {
    setOpenModal(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedRow(null);
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false, // In Worker.js, update the action column renderCell function:
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <div className="cell-btn">
              {view === "address" ? (
                <img src={AddressLocation} alt="" />
              ) : (
                <img
                  src={ViewUser}
                  alt=""
                  onClick={(e) => {
                    e.stopPropagation();
                    // If we're in logs view, show logDetails instead of userRates
                    if (view === "logs") {
                      handleOpen("logDetails", params.row);
                    } else if (view === "orders") {
                      navigate(`/orders/${params.row._id}`);
                    } else {
                      handleOpen("userRates", params.row);
                    }
                  }}
                />
              )}
            </div>

            <div
              className="cell-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleModalOpen("delete");
                setSelectedRow(params.row);
              }}
            >
              <img src={DeleteUser} alt="" />
            </div>
          </div>
        );
      },
    },
  ];

  const renderRatesView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={WorkerRatesColumns.concat(actionColumn)}
        rows={userRatings.map((row, index) => ({
          ...row,
          id: row._id || index,
          index: (page - 1) * pageSize + index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={viewLoading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "تقييم في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
        rowHeight={60}
      />
    </div>
  );

  const renderOrdersView = () => (
    <div className="container">
      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={ProductOrdersColumns.concat(actionColumn)}
        rows={userOrders.map((row, index) => ({
          ...row,
          id: row._id || index,
          index: (page - 1) * pageSize + index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={viewLoading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "طلب في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
        rowHeight={60}
      />
    </div>
  );

  const renderLogsView = () => (
    <div className="container">
      <TextField
        type="date"
        label="اختر التاريخ"
        value={selectedDate}
        onChange={(e) => {
          setSelectedDate(e.target.value);
          setPage(1); // Reset to first page when changing date
        }}
        sx={{ width: 200, marginBottom: "16px" }}
        size="small"
        InputLabelProps={{
          shrink: true,
          style: { textAlign: "right" },
        }}
        inputProps={{
          dir: "rtl",
        }}
      />

      <DataGrid
        sx={{ backgroundColor: "white", width: "100%" }}
        autoHeight={true}
        columns={WorkerLogsColumns.concat(actionColumn)}
        rows={userLogs.map((row, index) => ({
          ...row,
          id: row._id || index,
          index: (page - 1) * pageSize + index + 1,
        }))}
        disableVirtualization
        disableColumnFilter
        disableColumnMenu
        loading={viewLoading}
        pagination
        paginationMode="server"
        rowCount={totalCount}
        initialState={{
          pagination: {
            paginationModel: { pageSize, page: page - 1 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(model) => {
          // Handle page change
          if (model.page !== page - 1) {
            setPage(model.page + 1);
          }

          // Handle page size change
          if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
            setPage(1); // Reset to first page when changing page size
          }
        }}
        getRowClassName={(params) => "data-grid-row"}
        localeText={{
          MuiTablePagination: {
            labelRowsPerPage: "سجل في الصفحة",
          },
          noRowsLabel: "لا توجد بيانات",
        }}
        components={{
          NoRowsOverlay: () => (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                padding: "20px",
                color: "#666",
              }}
            >
              <p>لا توجد بيانات للعرض</p>
            </div>
          ),
        }}
        rowHeight={60}
      />
    </div>
  );

  const renderPapersView = () => (
    <div className="edit-partner-container">
      <p className="title">بطاقة الهوية - الوجه/الظهر</p>
      <div className="items-container">
        <div className="item">
          <span>صورة الوجه</span>
          <div className="img-container">
            <img src={photosApi + userData?.idFrontSide?.path} alt="" />
          </div>
        </div>

        <div className="item">
          <span>صورة الظهر</span>
          <div className="img-container">
            <img src={photosApi + userData?.idBackSide?.path} alt="" />
          </div>
        </div>
      </div>
    </div>
  );

  const views = {
    orders: renderOrdersView,
    rates: renderRatesView,
    papers: renderPapersView,
    logs: renderLogsView,
  };

  return (
    <div className="partner">
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="user-header">
            <div className="user-info">
              <div className="info-container">
                <p className="title">بيانات العامل</p>
                <div className="info">
                  <img src={photosApi + userData?.photo} alt="" />
                  <div className="wrapper">
                    <p className="name">{userData?.name}</p>
                    <span className="phone">
                      <Phone style={{ fontSize: "18px" }} />{" "}
                      {userData?.phoneNumber}
                    </span>
                    {userData?.alternativePhone && (
                      <span className="phone">
                        <Phone style={{ fontSize: "18px" }} />
                        {userData?.alternativePhone}
                      </span>
                    )}
                    <span className="phone">
                      <StarIcon style={{ color: "#F9A130" }} />
                      <span>{userData?.ratingAverage}</span>
                      <span>({userData?.ratingQuantity})</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="widgets">
                <div className="widget">
                  <div
                    className="icon-container"
                    style={{ backgroundColor: "#ECF9FF" }}
                  >
                    <img
                      src={FactoriesProductsGif}
                      alt=""
                      className="widget-icon"
                    />
                  </div>
                  <div className="widget-text-container">
                    <p className="number">{userData?.bookingsCount || 0}</p>
                  </div>

                  <span className="widget-title">عدد الطلبات</span>
                </div>
              </div>
            </div>

            <div className="btns-container">
              <GreenButton
                text="الطلبات"
                size={isMobile ? "full" : "medium"}
                variant={view === "orders" ? "fill" : "outlined"}
                onClick={() => {
                  setView("orders");
                  setPage(1); // Reset page when changing view
                }}
              />

              <GreenButton
                text="الاوراق"
                size={isMobile ? "full" : "medium"}
                variant={view === "papers" ? "fill" : "outlined"}
                onClick={() => {
                  setView("papers");
                }}
              />

              <GreenButton
                text="الحضور والانصراف"
                size={isMobile ? "full" : "medium"}
                variant={view === "logs" ? "fill" : "outlined"}
                onClick={() => {
                  setView("logs");
                  setPage(1); // Reset page when changing view
                }}
              />

              <GreenButton
                text="التقييمات"
                size={isMobile ? "full" : "medium"}
                variant={view === "rates" ? "fill" : "outlined"}
                onClick={() => {
                  setView("rates");
                  setPage(1); // Reset page when changing view
                }}
              />
            </div>
          </div>

          {views[view]()}
        </>
      )}

      <Modals
        open={openModal}
        currentView="delete"
        rout={`/ratings/${selectedRow?._id}`}
        deleteMessage="تم حذف هذا التقييم بنجاح"
        onClose={() => {
          setOpenModal(false);
        }}
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView={currentModalView}
        rowData={selectedRow} // Pass the selected row data to the SwipeableModals
      />
    </div>
  );
};

export default Worker;
